import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/layouts/MainLayout';
import { BunkerPriceAccordionItem, BunkerPriceSkeleton } from '@/components/bunkerPrices';
import { Button, Alert, Container, Stack, PageHeader, ProgressSpinner } from '@/components/ui';
import { useBunkerPricesStore } from '@/store/bunkerPricesStore';
import { RefreshCw } from 'lucide-react';
import type { HistoricalPeriod } from '@/types/bunkerPrices.types';

export function BunkerPricesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const portParam = searchParams.get('port');
  const periodParam = searchParams.get('period');

  const [expandedPort, setExpandedPort] = useState<number | null>(
    portParam ? parseInt(portParam, 10) : null
  );
  const [hasInitialized, setHasInitialized] = useState(false);

  const {
    loading,
    error,
    historyLoading,
    selectedPeriod,
    fetchBunkerPrices,
    refreshBunkerPrices,
    fetchBunkerPriceHistory,
    setSelectedPeriod,
    getGroupedPrices,
    history,
  } = useBunkerPricesStore();

  const groupedPrices = getGroupedPrices();

  // Fetch bunker prices on mount
  useEffect(() => {
    fetchBunkerPrices();
  }, [fetchBunkerPrices]);

  // Set period from URL on mount
  useEffect(() => {
    if (periodParam) {
      const period = parseInt(periodParam, 10);
      if ([0, 1, 2, 3].includes(period)) {
        setSelectedPeriod(period as HistoricalPeriod);
      }
    }
  }, [periodParam, setSelectedPeriod]);

  // Auto-expand first port when data loads if no port in URL (only on initial load)
  useEffect(() => {
    if (groupedPrices.length > 0 && !hasInitialized) {
      setHasInitialized(true);
      if (!portParam) {
        const firstPort = groupedPrices[0];
        setExpandedPort(firstPort.portId);
        fetchBunkerPriceHistory(firstPort.portId, selectedPeriod);
        setSearchParams({ port: firstPort.portId.toString(), period: selectedPeriod.toString() });
      } else {
        const portId = parseInt(portParam, 10);
        fetchBunkerPriceHistory(portId, selectedPeriod);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedPrices.length, hasInitialized]);

  const handleRefresh = async () => {
    await refreshBunkerPrices();
  };

  const handlePeriodChange = (period: HistoricalPeriod) => {
    setSelectedPeriod(period);
    if (expandedPort) {
      fetchBunkerPriceHistory(expandedPort, period);
      setSearchParams({ port: expandedPort.toString(), period: period.toString() });
    }
  };

  const periods = [
    { value: 0, label: 'Daily' },
    { value: 1, label: 'Weekly' },
    { value: 2, label: 'Monthly' },
    { value: 3, label: 'Yearly' },
  ];

  return (
    <MainLayout>
      <Container className="pb-8">
        <Stack direction="vertical" spacing={6}>
          {/* Page Header */}
          <PageHeader
            title="Bunker Prices"
            actionsInline
            actions={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={loading}
                title="Refresh bunker prices"
                aria-label="Refresh bunker prices"
                className="!min-h-9 !min-w-9 shrink-0 !px-2 !py-2"
              >
                {loading ? <ProgressSpinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            }
          />

          {/* Error Alert */}
          {error && (
            <Alert variant="error" title="Error loading bunker prices">
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading && groupedPrices.length === 0 && <BunkerPriceSkeleton />}

          {/* Bunker Prices Accordion */}
          {groupedPrices.length > 0 && (
            <div className="space-y-2">
              {groupedPrices.map((group) => (
                <BunkerPriceAccordionItem
                  key={group.portId}
                  group={group}
                  isExpanded={expandedPort === group.portId}
                  selectedPeriod={selectedPeriod}
                  historyData={expandedPort === group.portId ? history : []}
                  historyLoading={historyLoading}
                  onToggle={() => {
                    const newExpandedPort = expandedPort === group.portId ? null : group.portId;
                    setExpandedPort(newExpandedPort);
                    if (newExpandedPort) {
                      fetchBunkerPriceHistory(newExpandedPort, selectedPeriod);
                      setSearchParams({
                        port: newExpandedPort.toString(),
                        period: selectedPeriod.toString(),
                      });
                    } else {
                      searchParams.delete('port');
                      setSearchParams(searchParams);
                    }
                  }}
                  onPeriodChange={handlePeriodChange}
                  periods={periods}
                />
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && groupedPrices.length === 0 && (
            <div className="py-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                No bunker prices available
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Bunker price data is currently not available. Please check your connection and try
                again.
              </p>
              <div className="mt-6">
                <Button onClick={handleRefresh} size="sm">
                  Retry
                </Button>
              </div>
            </div>
          )}
        </Stack>
      </Container>
    </MainLayout>
  );
}
